/**
 * PasswordChangeForm Component
 * 
 * Form for changing user password with validation.
 * Requires old password and new password with confirmation.
 * 
 * Requirements: 8.2
 */

import React, { useState } from 'react';
import { validatePasswordStrength } from '../utils/validation.utils';
import { ErrorAlert } from './ErrorAlert';
import { LoadingSpinner } from './LoadingSpinner';
import { PasswordStrength } from './PasswordStrength';

export interface PasswordChangeFormProps {
  onSubmit: (oldPassword: string, newPassword: string) => Promise<void>;
  onCancel?: () => void;
}

export const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!oldPassword) {
      newErrors.push('Current password is required');
    }

    if (!newPassword) {
      newErrors.push('New password is required');
    } else {
      const strength = validatePasswordStrength(newPassword);
      if (!strength.isValid) {
        newErrors.push(...strength.feedback);
      }
    }

    if (!confirmPassword) {
      newErrors.push('Password confirmation is required');
    } else if (newPassword !== confirmPassword) {
      newErrors.push('Passwords do not match');
    }

    if (oldPassword && newPassword && oldPassword === newPassword) {
      newErrors.push('New password must be different from current password');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors([]);
    setShowSuccess(false);

    try {
      await onSubmit(oldPassword, newPassword);
      setShowSuccess(true);
      // Clear form
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Password change failed']);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 20px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.7 : 1,
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#95a5a6',
    marginLeft: '8px',
  };

  return (
    <form onSubmit={handleSubmit} className="password-change-form">
      {errors.length > 0 && <ErrorAlert message={errors} onDismiss={() => setErrors([])} />}
      
      {showSuccess && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724',
            marginBottom: '16px',
          }}
        >
          Password changed successfully!
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="old-password" style={labelStyle}>
          Current Password
        </label>
        <input
          id="old-password"
          type="password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="current-password"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="new-password" style={labelStyle}>
          New Password
        </label>
        <input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="new-password"
          style={inputStyle}
          aria-required="true"
        />
        {newPassword && <PasswordStrength password={newPassword} />}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="confirm-password" style={labelStyle}>
          Confirm New Password
        </label>
        <input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="new-password"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            style={cancelButtonStyle}
          >
            Cancel
          </button>
        )}
        <button type="submit" disabled={isLoading} style={buttonStyle}>
          {isLoading ? <LoadingSpinner size="small" /> : 'Change Password'}
        </button>
      </div>
    </form>
  );
};
